<template>

  <TransitionRoot :show="open" as="template">
    <Dialog as="div" class="fixed z-10 inset-0 xs:m-0.5 sm:m-4 xl:mx-24 xl:my-8" @close="$emit('close')">
      <div class="flex items-center justify-center p-4 text-center sm:block sm:p-0 h-full max-h-full">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-95 transition-opacity"/>
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <!--        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>-->
        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
              class="h-full flex flex-col bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle  mx-auto"
              style="">

            <!-- Close button-->
            <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button
                  class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  type="button"
                  @click="$emit('close')"
              >
                <span class="sr-only">Close</span>
                <XIcon aria-hidden="true" class="h-6 w-6"/>
              </button>
            </div>

            <!--header -->
            <div class="p-4 sm:px-6 flex-grow-0">
              <DialogTitle as="h3" class="flex items-center space-x-2 text-lg font-medium leading-6 text-gray-900">
                View the attachment named: &nbsp;<span class="font-semibold">{{ documentName }}</span>
              </DialogTitle>
              <p class="text-sm text-gray-500 pb-2"></p>
              <div class="border-b border-gray"></div>
            </div>

            <!--body -->
            <div class="px-4 pb-5 sm:px-6 flex-grow h-full max-h-full overflow-auto xl:overflow-hidden flex flex-col flex">
              <!-- Content -->
              <nav class="flex flex-col sm:flex-row items-center justify-center sm:space-x-12 py-3 rounded-t" id="pdf-controls" ref="observableForResizing">
                <div id="navigation_controls" class="flex flex-row justify-center space-x-3 w-full sm:w-auto">
                  <button id="go_previous"
                          :disabled="this.currentPage === 1"
                          @click="handlePreviousPage()"
                          :class="{'disabled': this.currentPage === 1}"
                          title="Previous">
                    <span class="sr-only">Previous</span>
                    <ArrowCircleUpIcon class="h-7 w-7"></ArrowCircleUpIcon>
                  </button>
                  <button id="go_next"
                          :disabled="this.currentPage === this.pageCount"
                          @click="handleNextPage()"
                          :class="{'disabled': this.currentPage === this.pageCount}"
                          title="Next">
                    <span class="sr-only">Next</span>
                    <ArrowCircleDownIcon class="h-7 w-7"></ArrowCircleDownIcon>
                  </button>
                  <div>
                    <input class="w-8 text-center bg-transparent"
                           style="border-width: 0px 0px 1px; border-color: #636363;"
                           @keyup.enter="handleCurrentPage($event)"
                           @change="handleCurrentPage($event)"
                           v-model="desiredPage"/>
                    <span> / {{ pageCount }}</span>
                  </div>
                </div>
                <div id="zoom_controls" class="flex flex-row justify-center space-x-3 w-full sm:w-auto mt-4 sm:mt-0">
                  <button id="zoom_out"
                          :disabled="this.zoom === this.zoomMin"
                          @click="handleZoomOut()"
                          :class="{'disabled': this.zoom === this.zoomMin}">
                    <ZoomOutIcon class="w-8 h-8"></ZoomOutIcon>
                  </button>
                  <button id="zoom_in"
                          :disabled="this.zoom === this.zoomMax"
                          @click="handleZoomIn()"
                          :class="{'disabled': this.zoom === this.zoomMax}">
                    <ZoomInIcon class="w-8 h-8"></ZoomInIcon>
                  </button>
                  <span>{{ Math.round(zoom * 100) }}%</span>
                  <!--                    <select id="current_zoom" :value="zoom * 100" type="number" step="25">-->
                  <!--                      <option value="1">100%</option>-->
                  <!--                    </select>-->
                </div>
              </nav>

              <div class="overflow-auto relative" ref="contentWrapper">

                <main class="px-2 sm:px-4" ref="mainViewerZone" id="pdf-wrapper" tabindex="1">
                  <vue-pdf-embed
                      id="pdf-embed"
                      ref="pdfRef"
                      :source="this.$props.src !== null ? this.$props.src : pdfSource"
                      :page="page"
                      @password-requested="handlePasswordRequest"
                      @rendered="handleDocumentRender"
                      :disable-text-layer="true"
                      :disable-annotation-layer="true"
                      :width="computedViewerZone"
                  />
                </main>
              </div>

              <div v-show="isLoading"  class="flex items-center justify-center space-x-2 h-full">
                <div class="animate-bounce w-3 h-3 bg-gray-500 rounded-full animate-circle-1" role="status"></div>
                <div class="animate-bounce w-3 h-3 bg-gray-500 rounded-full animate-circle-2" role="status"></div>
                <div class="animate-bounce w-3 h-3 bg-gray-500 rounded-full animate-circle-3" role="status"></div>
              </div>
              <!-- End Content -->
            </div>

            <!--footer -->
            <div class="p-4 sm:px-6 flex-grow-0 border-t ">
              <div class="flex items-end justify-end space-x-3 lg:space-x-6">
                <button
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                    type="button"
                    @click="$emit('close')">
                  Close
                </button>
              </div>
            </div>

          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

</template>


<script>

import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {ArrowCircleDownIcon, ArrowCircleUpIcon, XIcon, ZoomInIcon, ZoomOutIcon} from "@heroicons/vue/outline";
import vuePdfEmbed from 'vue-pdf-embed';
import SimpleLoader from "@/components/SimpleLoader";

export default {
  name: "PdfViewer",
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    VuePdfEmbed: vuePdfEmbed,
    XIcon,
    ZoomInIcon,
    ZoomOutIcon,
    ArrowCircleUpIcon,
    ArrowCircleDownIcon,
    SimpleLoader
  },
  props: {
    open: {
      type: Boolean,
      default: true,
    },
    src: {
      type: String,
      required: true,
    },
    documentName: {
      type: String,
      required: true,
    },
    zoomMin: {
      type: Number,
      default: 0.25
    },
    zoomMax: {
      type: Number,
      default: 3.
    },
  },
  data() {
    return {
      isLoading: true,
      page: null,
      pageCount: 1,
      pdfSource: this.$props.src,
      showAllPages: true,

      viewerZoneWidth: null,
      desiredPage: 1,
      currentPage: 1,
      zoom: 1,

      _defaultPdfWrapperWidth: null,
      _resizeObserver: null,

      _localRefs: {},
    }
  },
  mounted() {

  },
  beforeDestroy () {
    this._resizeObserver.unobserve(this.$refs.observableForResizing)
  },
  watch: {
    showAllPages() {
      this.page = this.showAllPages ? null : 1
    },
  },
  methods: {
    onResize() {
      if (this.$refs.observableForResizing === null) {
        return;
      }

      if (this.$refs.observableForResizing === undefined) {
        setTimeout(() => this.onResize(), 50);
        return;
      }

      let width = this.$refs.observableForResizing.clientWidth;
      if (width === this._defaultPdfWrapperWidth) {
        return;
      }


      const refreshFcn = () => {
        if (!this.isLoading) {
          this.$nextTick(() => {
            this.viewerZoneWidth = width - 32;
            this._defaultPdfWrapperWidth = width;
            this.zoom = this._defaultPdfWrapperWidth > 1000 ? (Math.round((1000.0 / this._defaultPdfWrapperWidth) * 100) / 100) : 1;
            this.$refs.mainViewerZone.style.width = this._defaultPdfWrapperWidth + 'px';
          });
          return;
        }
        setTimeout(refreshFcn, 20);
      };

      setTimeout(refreshFcn, 20)
    },

    handleDocumentRender() {
      console.log('Rendered')
      this.isLoading = false;
      this.pageCount = this.$refs.pdfRef.pageCount || 1;

      this.$nextTick(function () {
        if(this._resizeObserver === null) {
          this._resizeObserver = new ResizeObserver(this.onResize);
          this._resizeObserver.observe(this.$refs.observableForResizing);
        }
      }.bind(this))
    },

    handlePasswordRequest(callback, retry) {
      callback(prompt(retry
          ? 'Enter password again'
          : 'Enter password'
      ))
    },

    handleNextPage() {
      const desiredPage = this.currentPage + 1;
      if (this.pdfSource == null || desiredPage > this.pageCount) {
        return;
      }
      this.desiredPage = desiredPage;
      this.handleCurrentPage();
    },
    handlePreviousPage() {
      const desiredPage = this.currentPage - 1;
      if (this.pdfSource == null || this.currentPage === 1) {
        return;
      }
      this.desiredPage = this.currentPage - 1;
      this.handleCurrentPage();
    },

    handleCurrentPage() {
      if (this.pdfSource == null) {
        return;
      }

      if (this.desiredPage >= 1 && this.desiredPage <= this.pageCount) {
        this.currentPage = this.desiredPage;
        this.$refs.mainViewerZone.querySelector(`#pdf-embed-${this.currentPage}`).scrollIntoView({
          behavior: 'smooth',
          block: "start",
          inline: "nearest"
        });
      }
    },

    handleZoomIn() {
      if(this.isLoading) {
        return;
      }

      this.isLoading = true;

      if (this.pdfSource == null) {
        return;
      }

      this.zoom = Math.min(this.zoom + 0.25, this.zoomMax);

      this.$nextTick(() => {
        if(this.$refs.mainViewerZone !== undefined && this.$refs.mainViewerZone !== null) {
          const canvasEl = this.$refs.mainViewerZone.querySelector('#pdf-embed-1>canvas');
          if(canvasEl !== null) {
            const width = canvasEl.clientWidth;
            if (this.zoom > 1) {
              this.$refs.mainViewerZone.style.width = (32 + width) + 'px';
              // console.log('zoom > 1: w -> ' + this.$refs.mainViewerZone.style.width);
            } else if (this.zoom <= 1) {
              this.$refs.mainViewerZone.style.width = (this._defaultPdfWrapperWidth + 16) + 'px';
              // console.log('zoom <= 1: w -> ' + this.$refs.mainViewerZone.style.width);
            }
          }
        }
      });

    },

    handleZoomOut() {
      if(this.isLoading) {
        return;
      }

      this.isLoading = true;

      if (this.pdfSource == null) {
        return;
      }

      this.zoom = Math.max(this.zoom - 0.25, this.zoomMin);

      this.$nextTick(() => {
        if(this.$refs.mainViewerZone !== undefined && this.$refs.mainViewerZone !== null) {
          const canvasEl = this.$refs.mainViewerZone.querySelector('#pdf-embed-1>canvas');
          if(canvasEl !== null) {
            const width = this.$refs.mainViewerZone.querySelector('#pdf-embed-1>canvas').clientWidth;
            if (this.zoom > 1) {
              this.$refs.mainViewerZone.style.width = (32 + width) + 'px';
              // console.log('zoom > 1: w -> ' + this.$refs.mainViewerZone.style.width);
            } else {
              this.$refs.mainViewerZone.style.width = this._defaultPdfWrapperWidth + 'px';
              // console.log('zoom <= 1: w -> ' + this.$refs.mainViewerZone.style.width);
            }
          }
        }
      });
    },
  },

  computed: {
    computedViewerZone() {
      return Math.ceil(this.zoom * this.viewerZoneWidth);
    }
  }
}
</script>


<style lang="scss">
#pdf-wrapper {
  background-color: #282828;
}

#pdf-controls {
  //color: rgb(77, 77, 77);
  color: #C2C2C2;
  background-color: #4D4D4D;
}

#pdf-embed {
  canvas {
    margin: auto auto 0.5rem auto;
  }
  div:last-child canvas{
    margin: auto;
  }
}

button.disabled {
  color: rgb(136, 136, 136);
}

.animate-circle-1{
  animation-delay: 0.1s !important;
}
.animate-circle-2{
  animation-delay: 0.2s !important;
}
.animate-circle-3{
  animation-delay: 0.3s !important;
}
</style>
